<template>
  <div class="dte-visual-preview">
    <!-- Encabezado de documento -->
    <div class="preview-document elevation-3">

      <!-- Header: empresa + título documento -->
      <div class="preview-header d-flex justify-space-between align-start pa-6">
        <div>
          <div class="text-h6 font-weight-black primary--text">{{ companyName }}</div>
          <div class="caption grey--text">{{ companyGiro }}</div>
          <div class="caption grey--text">RUT: {{ companyRut }}</div>
        </div>
        <div class="text-right">
          <div class="doc-type-badge">
            <div class="doc-type-label">FACTURA ELECTRÓNICA</div>
            <div class="doc-type-label">PROFORMA</div>
            <div class="doc-folio">N° —</div>
          </div>
        </div>
      </div>

      <v-divider class="mx-6" />

      <!-- Datos del receptor -->
      <div class="pa-6 pb-4">
        <div class="preview-section-title mb-3">RECEPTOR</div>
        <v-row dense>
          <v-col cols="6">
            <div class="preview-label">Razón Social</div>
            <div class="preview-value">{{ rznSocRecep || '—' }}</div>
          </v-col>
          <v-col cols="3">
            <div class="preview-label">RUT</div>
            <div class="preview-value">{{ rutRecep || '—' }}</div>
          </v-col>
          <v-col cols="3">
            <div class="preview-label">Giro</div>
            <div class="preview-value">{{ giroRecep || '—' }}</div>
          </v-col>
          <v-col cols="6" class="mt-2">
            <div class="preview-label">Dirección</div>
            <div class="preview-value">{{ dirRecep || '—' }}</div>
          </v-col>
          <v-col cols="3" class="mt-2">
            <div class="preview-label">Comuna</div>
            <div class="preview-value">{{ cmnaRecep || '—' }}</div>
          </v-col>
          <v-col cols="3" class="mt-2">
            <div class="preview-label">Fecha Emisión</div>
            <div class="preview-value">{{ today }}</div>
          </v-col>
          <v-col v-if="endDate" cols="3" class="mt-2">
            <div class="preview-label">Vencimiento</div>
            <div class="preview-value accent--text font-weight-black">
              {{ endDateFormatted }}
              <span v-if="endDateDays !== null" class="caption grey--text font-weight-medium ml-1">
                ({{ endDateDays }} días)
              </span>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Referencia a OT -->
      <div v-if="otTitle" class="px-6 pb-3">
        <div class="ot-reference">
          <v-icon x-small class="mr-1 accent--text">fas fa-link</v-icon>
          <span class="caption font-weight-bold accent--text">Referencia OT:</span>
          <span class="caption ml-1 grey--text text--darken-2">{{ otTitle }}</span>
        </div>
      </div>

      <v-divider class="mx-6" />

      <!-- Tabla de líneas -->
      <div class="px-6 pt-4">
        <table class="preview-table">
          <thead>
            <tr>
              <th class="text-left">Código</th>
              <th class="text-left" style="width:42%">Descripción</th>
              <th class="text-center">Cant.</th>
              <th class="text-right">P. Unit.</th>
              <th class="text-right">Total</th>
              <th class="text-center" style="width:60px">IVA</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, i) in lines" :key="i" :class="i % 2 === 0 ? 'row-even' : 'row-odd'">
              <td class="preview-sku">{{ line.sku || '—' }}</td>
              <td class="preview-name">{{ line.name || '—' }}</td>
              <td class="text-center preview-qty">{{ line.qty }}</td>
              <td class="text-right preview-price">{{ line.price | moneda }}</td>
              <td class="text-right preview-total font-weight-bold">{{ lineTotal(line) | moneda }}</td>
              <td class="text-center">
                <v-chip x-small :color="line.afecto ? 'accent lighten-4' : 'grey lighten-3'" class="font-weight-bold">
                  {{ line.afecto ? 'AFT' : 'EXN' }}
                </v-chip>
              </td>
            </tr>
            <tr v-if="lines.length === 0">
              <td colspan="6" class="text-center grey--text caption py-6">
                Sin líneas de detalle
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Totales -->
      <div class="d-flex justify-end pa-6 pt-4">
        <div class="totals-box">
          <div class="totals-row">
            <span class="totals-label">Neto Bruto Afecto</span>
            <span class="totals-amount">{{ netoAfecto | moneda }}</span>
          </div>
          <div v-if="netoExento > 0" class="totals-row">
            <span class="totals-label">Exento</span>
            <span class="totals-amount">{{ netoExento | moneda }}</span>
          </div>
          <div v-if="globalDiscount > 0" class="totals-row">
            <span class="totals-label">Dcto. Global ({{ globalDiscount }}%)</span>
            <span class="totals-amount error--text">− {{ discountAmount | moneda }}</span>
          </div>
          <div class="totals-row">
            <span class="totals-label">IVA ({{ taxRate }}%)</span>
            <span class="totals-amount">{{ ivaAmount | moneda }}</span>
          </div>
          <v-divider class="my-2" />
          <div class="totals-row totals-grand">
            <span class="totals-label-grand">TOTAL</span>
            <span class="totals-amount-grand">{{ totalAmount | moneda }}</span>
          </div>
        </div>
      </div>

      <!-- Footer proforma -->
      <div class="preview-footer pa-4 text-center">
        <div class="caption grey--text">
          <v-icon x-small class="mr-1 warning--text">fas fa-exclamation-triangle</v-icon>
          Este es un documento de <strong>PREVISUALIZACIÓN</strong>. No tiene validez tributaria hasta que se genere el folio oficial.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'DTEVisualPreview',

  props: {
    lines: {
      type: Array,
      default: () => [],
    },
    rutRecep: { type: String, default: '' },
    rznSocRecep: { type: String, default: '' },
    giroRecep: { type: String, default: '' },
    dirRecep: { type: String, default: '' },
    cmnaRecep: { type: String, default: '' },
    otTitle: { type: String, default: '' },
    taxRate: { type: Number, default: 19 },
    globalDiscount: { type: Number, default: 0 },
    endDate: { type: String, default: null },
  },

  computed: {
    today() {
      return moment().format('DD/MM/YYYY')
    },
    endDateFormatted() {
      if (!this.endDate) return ''
      const [y, m, d] = this.endDate.split('-')
      return `${d}/${m}/${y}`
    },
    endDateDays() {
      if (!this.endDate) return null
      const diff = moment(this.endDate).diff(moment().startOf('day'), 'days')
      return diff
    },
    companyName() {
      return this.$store.getters.getCompany?.name || 'Mi Empresa S.A.'
    },
    companyGiro() {
      return this.$store.getters.getCompany?.giro || ''
    },
    companyRut() {
      return this.$store.getters.getCompany?.rut || ''
    },
    netoAfecto() {
      return this.lines
        .filter(l => l.afecto !== false)
        .reduce((acc, l) => acc + this.lineTotal(l), 0)
    },
    netoExento() {
      return this.lines
        .filter(l => l.afecto === false)
        .reduce((acc, l) => acc + this.lineTotal(l), 0)
    },
    discountAmount() {
      const disc = parseFloat(this.globalDiscount || 0)
      return Math.round((this.netoAfecto + this.netoExento) * disc / 100)
    },
    netoAfectoConDescuento() {
      const disc = parseFloat(this.globalDiscount || 0)
      return Math.round(this.netoAfecto * (1 - disc / 100))
    },
    netoExentoConDescuento() {
      const disc = parseFloat(this.globalDiscount || 0)
      return Math.round(this.netoExento * (1 - disc / 100))
    },
    ivaAmount() {
      return Math.round(this.netoAfectoConDescuento * this.taxRate * 0.01)
    },
    totalAmount() {
      return this.netoAfectoConDescuento + this.netoExentoConDescuento + this.ivaAmount
    },
  },

  methods: {
    lineTotal(line) {
      return Math.round(parseFloat(line.qty || 0) * parseFloat(line.price || 0))
    },
  },
}
</script>

<style scoped>
.dte-visual-preview {
  font-family: 'Lato', sans-serif;
}

.preview-document {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e7ef;
}

/* Header del doc */
.preview-header {
  background: linear-gradient(135deg, #f8fafc 0%, #eef6f7 100%);
}

.doc-type-badge {
  background: #1A1F2E;
  color: white;
  padding: 12px 20px;
  border-radius: 6px;
  text-align: center;
  min-width: 160px;
}
.doc-type-label {
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 1.5px;
  text-transform: uppercase;
}
.doc-folio {
  font-size: 1.1rem;
  font-weight: 900;
  margin-top: 4px;
  color: #35BDC8;
}

/* Sección receptor */
.preview-section-title {
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 2px;
  color: #35BDC8;
  text-transform: uppercase;
}
.preview-label {
  font-size: 0.68rem;
  color: #90a4ae;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.preview-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1A1F2E;
}

.ot-reference {
  background: #eef6f7;
  border-radius: 4px;
  padding: 4px 10px;
  display: inline-flex;
  align-items: center;
}

/* Tabla de líneas */
.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}
.preview-table thead tr {
  background: #f0f4f7;
}
.preview-table th {
  padding: 8px 10px;
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #546e7a;
  border-bottom: 2px solid #e0e7ef;
}
.preview-table td {
  padding: 9px 10px;
  border-bottom: 1px solid #f0f4f7;
  vertical-align: middle;
}
.row-even { background: white; }
.row-odd  { background: #fafbfc; }

.preview-sku  { font-weight: 700; color: #35BDC8; font-size: 0.78rem; }
.preview-name { color: #1A1F2E; }
.preview-qty { color: #607d8b; }
.preview-price { color: #607d8b; font-variant-numeric: tabular-nums; }
.preview-total { color: #1A1F2E; font-variant-numeric: tabular-nums; }

/* Totales */
.totals-box {
  min-width: 240px;
}
.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}
.totals-label {
  font-size: 0.8rem;
  color: #607d8b;
  font-weight: 600;
}
.totals-amount {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1A1F2E;
  font-variant-numeric: tabular-nums;
}
.totals-grand {
  padding-top: 6px;
}
.totals-label-grand {
  font-size: 0.9rem;
  font-weight: 900;
  color: #1A1F2E;
  letter-spacing: 1px;
  text-transform: uppercase;
}
.totals-amount-grand {
  font-size: 1.4rem;
  font-weight: 900;
  color: #1A1F2E;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.5px;
}

/* Footer */
.preview-footer {
  background: #fffbeb;
  border-top: 1px dashed #e0c050;
}
</style>
